import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { platformService } from "./platform";
import { report } from "./Sentry";

let isInitialized = false;

export const liveUpdateService = {
  /**
   * Initialize the live update service.
   * Must be called early in app lifecycle - notifyAppReady() must complete
   * within appReadyTimeout (10s) or the app will rollback to previous version.
   */
  async initialize(): Promise<void> {
    if (isInitialized) {
      console.log("[LiveUpdate] Already initialized, skipping");
      return;
    }

    if (!platformService.isNative()) {
      console.log("[LiveUpdate] Skipping - not running on native platform");
      return;
    }

    isInitialized = true;

    try {
      await CapacitorUpdater.notifyAppReady();
      console.log("[LiveUpdate] App ready notified successfully");
    } catch (error) {
      console.error("[LiveUpdate] Failed to notify app ready:", error);
      report(error);
    }

    this.setupEventListeners();
  },

  /**
   * Setup event listeners for update lifecycle events
   */
  setupEventListeners(): void {
    CapacitorUpdater.addListener("updateAvailable", (info) => {
      console.log("[LiveUpdate] Update available:", info.bundle.version);
    });

    CapacitorUpdater.addListener("downloadComplete", (info) => {
      console.log("[LiveUpdate] Download complete:", info.bundle.version);
    });

    CapacitorUpdater.addListener("downloadFailed", (info) => {
      console.error("[LiveUpdate] Download failed:", info.version);
      report(new Error(`Live update download failed: ${info.version}`), {
        capgoEvent: "downloadFailed",
        info,
      });
    });

    CapacitorUpdater.addListener("updateFailed", (info) => {
      console.error("[LiveUpdate] Update failed:", info.bundle.version);
      report(new Error(`Live update failed: ${info.bundle.version}`), {
        capgoEvent: "updateFailed",
        info,
      });
    });
  },

  /**
   * Get current bundle version info
   */
  async getCurrentVersion(): Promise<string | null> {
    if (!platformService.isNative()) {
      return null;
    }

    try {
      const current = await CapacitorUpdater.current();
      return current.bundle.version;
    } catch (error) {
      console.error("[LiveUpdate] Failed to get current version:", error);
      report(error);
      return null;
    }
  },
};
